import type {
  OrchestrationThread,
  OrchestrationThreadTimelineDelta,
  OrchestrationThreadTimelineProjection,
  OrchestrationThreadTimelineWindow,
  OrchestrationTimelineItem,
  OrchestrationTimelineItemRef,
} from "@t3tools/contracts";

function timelineItemKey(item: OrchestrationTimelineItem): string {
  switch (item.kind) {
    case "message-part":
      return `message-part:${item.part.id}`;
    case "message":
      return `message:${item.message.id}`;
    case "activity":
      return `activity:${item.activity.id}`;
    case "proposed-plan":
      return `proposed-plan:${item.proposedPlan.id}`;
  }
}

function timelineItemRefKey(item: OrchestrationTimelineItemRef): string {
  return `${item.kind}:${item.id}`;
}

function dedupeTimelineItems(
  items: ReadonlyArray<OrchestrationTimelineItem>,
): ReadonlyArray<OrchestrationTimelineItem> {
  const positions = new Map<string, number>();
  const output: Array<OrchestrationTimelineItem> = [];
  for (const item of items) {
    const key = timelineItemKey(item);
    const existing = positions.get(key);
    if (existing === undefined) {
      positions.set(key, output.length);
      output.push(item);
    } else {
      output[existing] = item;
    }
  }
  return output;
}

/** Materialize the existing UI shape from the thin projection and window items. */
export function materializeTimelineWindow(
  window: OrchestrationThreadTimelineWindow,
): OrchestrationThread & { readonly timelineItems: ReadonlyArray<OrchestrationTimelineItem> } {
  const messages = [] as Array<OrchestrationThread["messages"][number]>;
  const activities = [] as Array<OrchestrationThread["activities"][number]>;
  const proposedPlans = [] as Array<OrchestrationThread["proposedPlans"][number]>;

  const messagePositions = new Map<string, number>();
  for (const item of window.items) {
    switch (item.kind) {
      case "message-part":
        if (item.part.kind === "text") {
          const existing = messagePositions.get(item.part.messageId);
          if (existing === undefined) {
            messagePositions.set(item.part.messageId, messages.length);
            messages.push({
              id: item.part.messageId,
              role: item.part.role,
              text: item.part.text,
              turnId: item.part.turnId,
              streaming: item.part.streaming,
              createdAt: item.part.createdAt,
              updatedAt: item.part.completedAt ?? item.part.createdAt,
            });
          } else {
            const message = messages[existing];
            if (message !== undefined) {
              messages[existing] = {
                ...message,
                text: `${message.text}${item.part.text}`,
                streaming: item.part.streaming,
                updatedAt: item.part.completedAt ?? item.part.createdAt,
              };
            }
          }
        }
        break;
      case "message":
        messages.push(item.message);
        break;
      case "activity":
        activities.push(item.activity);
        break;
      case "proposed-plan":
        proposedPlans.push(item.proposedPlan);
        break;
    }
  }

  return {
    ...window.projection,
    messages,
    activities,
    proposedPlans,
    timelineItems: window.items,
    checkpoints: window.projection.checkpoints ?? [],
  };
}

/**
 * Prepend an authoritative `before` page. Opaque cursors are never interpreted
 * by the client and a response from another epoch is discarded.
 */
export function mergeTimelineBeforeWindow(
  current: OrchestrationThreadTimelineWindow,
  older: OrchestrationThreadTimelineWindow,
): OrchestrationThreadTimelineWindow {
  if (current.threadId !== older.threadId || current.epoch !== older.epoch) {
    return current;
  }
  return {
    ...current,
    beforeCursor: older.beforeCursor,
    hasOlder: older.hasOlder,
    items: dedupeTimelineItems([...older.items, ...current.items]),
  };
}

export type ApplyTimelineDeltaResult =
  | {
      readonly kind: "updated";
      readonly window: OrchestrationThreadTimelineWindow;
    }
  | { readonly kind: "epoch-mismatch" };

/** Apply a stable-id live delta without sequence or delivery replay state. */
export function applyTimelineDelta(
  current: OrchestrationThreadTimelineWindow,
  delta: OrchestrationThreadTimelineDelta,
): ApplyTimelineDeltaResult {
  if (current.threadId !== delta.threadId || current.epoch !== delta.epoch) {
    return { kind: "epoch-mismatch" };
  }

  const removed = new Set(delta.removedItems.map(timelineItemRefKey));
  const items = current.items.filter((item) => !removed.has(timelineItemKey(item)));

  return {
    kind: "updated",
    window: {
      ...current,
      ...(Object.hasOwn(delta, "beforeCursor") ? { beforeCursor: delta.beforeCursor! } : {}),
      ...(Object.hasOwn(delta, "hasOlder") ? { hasOlder: delta.hasOlder! } : {}),
      ...(delta.projection !== undefined
        ? { projection: { ...current.projection, ...delta.projection } }
        : {}),
      items: dedupeTimelineItems([...items, ...delta.upsertedItems]),
    },
  };
}

export function projectionFromThread(
  thread: OrchestrationThread,
): OrchestrationThreadTimelineProjection {
  return {
    id: thread.id,
    projectId: thread.projectId,
    title: thread.title,
    modelSelection: thread.modelSelection,
    runtimeMode: thread.runtimeMode,
    interactionMode: thread.interactionMode,
    branch: thread.branch,
    worktreePath: thread.worktreePath,
    latestTurn: thread.latestTurn,
    createdAt: thread.createdAt,
    updatedAt: thread.updatedAt,
    archivedAt: thread.archivedAt,
    settledOverride: thread.settledOverride,
    settledAt: thread.settledAt,
    snoozedUntil: thread.snoozedUntil ?? null,
    snoozedAt: thread.snoozedAt ?? null,
    deletedAt: thread.deletedAt,
    queuedTurns: thread.queuedTurns ?? [],
    checkpoints: thread.checkpoints,
    session: thread.session,
    bindingState: thread.bindingState,
  };
}
